import { useEffect, useRef, useState } from "react";
import { Menu, X, Mail, ChevronDown, UserRound, Building2, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

/**
 * Site nav — grouped dropdowns keep the top bar tidy while the "Sole trader"
 * pill and the two right-hand CTAs stay prominent. Dropdowns open on hover
 * (desktop) and on click (touch); mobile falls back to a single stacked panel.
 */
const GROUPS = [
  {
    label: "Company",
    items: [
      { href: "/#features", label: "Why us" },
      { href: "/#how", label: "How it works" },
      { href: "/about", label: "About the team" },
      { href: "/#faq", label: "FAQ" },
    ],
  },
  {
    label: "Services & pricing",
    items: [
      { href: "/#pricing", label: "Pricing plans" },
      { href: "/#clientops", label: "ClientOps portal" },
      { href: "/#free-calculator", label: "Free tax calculator" },
      { href: "/#learn", label: "Free guides" },
      { href: "/rentals", label: "Rental accounts" },
    ],
  },
];

export default function Nav({ onCtaClick, onContactClick }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const closeTimer = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Delay-close so the pointer can travel from the trigger to the panel
  const hoverOpen = (label) => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpenDropdown(label);
  };
  const hoverClose = () => {
    closeTimer.current = setTimeout(() => setOpenDropdown(null), 160);
  };

  return (
    <header
      className={`sticky top-0 z-50 transition-all ${
        scrolled
          ? "backdrop-blur-xl bg-white/80 border-b border-slate-200/60"
          : "bg-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-4 flex items-center justify-between gap-4">
        <a href="#top" className="flex items-center gap-3 flex-shrink-0" data-testid="nav-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_small-biz-tax-hub/artifacts/gg58om9x_NXTAXLOGOW.2022.png"
            alt="NZ Taxation Limited"
            className="h-10 w-10 object-contain"
          />
          <div className="leading-tight">
            <div className="font-heading font-black text-[15px]">NZ Taxation</div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">
              Limited
            </div>
          </div>
        </a>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-2 lg:gap-4">
          {GROUPS.map((g) => (
            <div
              key={g.label}
              className="relative"
              onMouseEnter={() => hoverOpen(g.label)}
              onMouseLeave={hoverClose}
            >
              <button
                type="button"
                onClick={() => setOpenDropdown((v) => (v === g.label ? null : g.label))}
                className="inline-flex items-center gap-1 font-heading font-medium text-sm text-slate-700 hover:text-[#C9A349] transition-colors px-3 py-2"
                data-testid={`nav-dropdown-${g.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                aria-expanded={openDropdown === g.label}
              >
                {g.label}
                <ChevronDown
                  className={`w-3.5 h-3.5 transition-transform ${
                    openDropdown === g.label ? "rotate-180" : ""
                  }`}
                />
              </button>
              {openDropdown === g.label && (
                <div
                  className="absolute top-full left-0 mt-1 min-w-[220px] bg-white border border-slate-200 shadow-lg py-2"
                  onMouseEnter={() => hoverOpen(g.label)}
                  onMouseLeave={hoverClose}
                >
                  {g.items.map((it) => (
                    <NavItem
                      key={it.href}
                      to={it.href}
                      onClick={() => setOpenDropdown(null)}
                    >
                      {it.label}
                    </NavItem>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Prominent entity picker — Sole trader / Company */}
          <div
            className="relative"
            onMouseEnter={() => hoverOpen("__entity")}
            onMouseLeave={hoverClose}
          >
            <button
              type="button"
              onClick={() => setOpenDropdown((v) => (v === "__entity" ? null : "__entity"))}
              className="ml-1 inline-flex items-center gap-2 border-2 border-[#C9A349] text-[#0A192F] px-4 py-2 text-sm font-heading font-bold uppercase tracking-[0.12em] hover:bg-[#C9A349] transition-colors"
              data-testid="nav-entity-picker"
              aria-expanded={openDropdown === "__entity"}
            >
              <UserRound className="w-4 h-4" />
              For your business
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${
                  openDropdown === "__entity" ? "rotate-180" : ""
                }`}
              />
            </button>
            {openDropdown === "__entity" && (
              <div
                className="absolute top-full right-0 mt-1 min-w-[260px] bg-white border border-slate-200 shadow-lg py-2"
                onMouseEnter={() => hoverOpen("__entity")}
                onMouseLeave={hoverClose}
              >
                <Link
                  to="/sole-traders"
                  onClick={() => setOpenDropdown(null)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 group"
                  data-testid="nav-entity-sole-trader"
                >
                  <UserRound className="w-4 h-4 mt-0.5 text-[#C9A349] flex-shrink-0" />
                  <div>
                    <div className="font-heading font-bold text-sm text-[#0A192F] group-hover:text-[#C9A349]">
                      I&apos;m a sole trader
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      Contractor, tradie, freelancer or self-employed
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 ml-auto mt-1 text-slate-400 group-hover:text-[#C9A349]" />
                </Link>
                <Link
                  to="/companies"
                  onClick={() => setOpenDropdown(null)}
                  className="flex items-start gap-3 px-4 py-3 hover:bg-slate-50 group border-t border-slate-100"
                  data-testid="nav-entity-company"
                >
                  <Building2 className="w-4 h-4 mt-0.5 text-[#C9A349] flex-shrink-0" />
                  <div>
                    <div className="font-heading font-bold text-sm text-[#0A192F] group-hover:text-[#C9A349]">
                      We&apos;re a company
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5">
                      2-owner Ltd, husband-wife shop, partnership
                    </div>
                  </div>
                  <ArrowRight className="w-3.5 h-3.5 ml-auto mt-1 text-slate-400 group-hover:text-[#C9A349]" />
                </Link>
              </div>
            )}
          </div>

          <button
            onClick={onContactClick}
            className="group relative inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-4 py-2.5 text-sm font-heading font-bold uppercase tracking-[0.12em] hover:bg-[#0A192F] hover:text-[#C9A349] transition-colors"
            data-testid="nav-contact-cta"
          >
            <Mail className="w-4 h-4" />
            Email us
            <span
              aria-hidden
              className="absolute -inset-0.5 border border-[#C9A349]/40 pointer-events-none"
            />
          </button>
          <button
            onClick={onCtaClick}
            className="bg-[#0A192F] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#C9A349] transition-colors"
            data-testid="nav-cta"
          >
            See plans
          </button>
        </nav>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          data-testid="nav-mobile-toggle"
        >
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Mobile panel */}
      {open && (
        <div className="md:hidden border-t border-slate-200 bg-white" data-testid="nav-mobile-panel">
          <div className="px-6 py-5 flex flex-col gap-5">
            <div className="grid grid-cols-2 gap-3">
              <Link
                to="/sole-traders"
                onClick={() => setOpen(false)}
                className="inline-flex flex-col items-center justify-center gap-1 border-2 border-[#C9A349] text-[#0A192F] px-3 py-3 font-heading font-black uppercase tracking-[0.12em] text-xs"
                data-testid="nav-mobile-sole-trader"
              >
                <UserRound className="w-4 h-4" /> Sole trader
              </Link>
              <Link
                to="/companies"
                onClick={() => setOpen(false)}
                className="inline-flex flex-col items-center justify-center gap-1 border-2 border-[#C9A349] text-[#0A192F] px-3 py-3 font-heading font-black uppercase tracking-[0.12em] text-xs"
                data-testid="nav-mobile-company"
              >
                <Building2 className="w-4 h-4" /> Company
              </Link>
            </div>

            {GROUPS.map((g) => (
              <div key={g.label}>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
                  {g.label}
                </div>
                <div className="flex flex-col">
                  {g.items.map((it) => (
                    <NavItem
                      key={it.href}
                      to={it.href}
                      onClick={() => setOpen(false)}
                      compact
                    >
                      {it.label}
                    </NavItem>
                  ))}
                </div>
              </div>
            ))}

            <button
              onClick={() => {
                setOpen(false);
                onContactClick?.();
              }}
              className="inline-flex items-center justify-center gap-2 bg-[#C9A349] text-[#0A192F] px-5 py-3 text-sm font-heading font-bold uppercase tracking-[0.12em]"
              data-testid="nav-mobile-contact-cta"
            >
              <Mail className="w-4 h-4" /> Email us
            </button>
            <button
              onClick={() => {
                setOpen(false);
                onCtaClick?.();
              }}
              className="bg-[#0A192F] text-white px-5 py-3 text-sm font-medium"
              data-testid="nav-mobile-cta"
            >
              See plans
            </button>
          </div>
        </div>
      )}
    </header>
  );
}

function NavItem({ to, onClick, compact, children }) {
  // Internal routes use <Link>; hash + external stay as anchors
  const isRouterPath = to.startsWith("/") && !to.startsWith("/#");
  const cls = compact
    ? "font-heading font-medium py-2 text-sm text-slate-700"
    : "block px-4 py-2 text-sm font-heading font-medium text-slate-700 hover:bg-slate-50 hover:text-[#C9A349] transition-colors";
  if (isRouterPath) {
    return (
      <Link to={to} onClick={onClick} className={cls}>
        {children}
      </Link>
    );
  }
  return (
    <a href={to} onClick={onClick} className={cls}>
      {children}
    </a>
  );
}
